<div class="assist">

  <!-- ── Header ───────────────────────────────────────────────── -->
  <header class="assist__header">
    <div>
      <h1 class="assist__title">
        <i class="fa-solid fa-circle-question assist__title-icon"></i>
        {{ 'ASSISTANCE.ADMIN_TITLE' | translate }}
      </h1>
      <p class="assist__subtitle">{{ 'ASSISTANCE.ADMIN_SUBTITLE' | translate }}</p>
    </div>
    <div class="assist__header-actions">
      <button class="btn-secondary" type="button" (click)="previewAsUser()" [disabled]="hasPendingOrder()">
        <i class="fa-solid fa-eye"></i>
        {{ 'ASSISTANCE.PREVIEW_AS_USER' | translate }}
      </button>
      <button class="btn-primary" type="button" (click)="openCreateResource()" [disabled]="loading() || errored() || hasPendingOrder()">
        <i class="fa-solid fa-plus"></i>
        {{ 'ASSISTANCE.ADD' | translate }}
      </button>
    </div>
  </header>

  <!-- ── Échec d'écriture ─────────────────────────────────────── -->
  <div class="assist__alert" *ngIf="actionError() as errorKey" role="alert">
    <i class="fa-solid fa-triangle-exclamation"></i>
    <span>{{ errorKey | translate }}</span>
    <button type="button" (click)="actionError.set(null)" [attr.aria-label]="'ACTIONS.CLOSE' | translate">
      <i class="fa-solid fa-xmark"></i>
    </button>
  </div>

  <ng-container *ngIf="!loading() && !errored()">
    <!-- ── Un onglet par public ────────────────────────────────── -->
    <nav class="tabs" role="tablist">
      <button *ngFor="let a of audiences" type="button" role="tab" class="tab"
              [class.is-active]="tab() === a" [attr.aria-selected]="tab() === a"
              (click)="setTab(a)">
        <i [class]="audienceIcon(a)"></i>
        {{ audienceLabel(a) | translate }}
        <span class="tab__count">{{ modulesOf(a).length }}</span>
      </button>
    </nav>

    <div class="tab-note" *ngIf="tab() === 'Admin'">
      <i class="fa-solid fa-user-shield"></i>
      {{ 'ASSISTANCE.TAB_NOTE_ADMIN' | translate }}
    </div>

    <div class="toolbar">
      <div class="assist__search">
        <i class="fa-solid fa-magnifying-glass"></i>
        <input type="search"
               [value]="query()"
               (input)="query.set($any($event.target).value)"
               [placeholder]="'ASSISTANCE.SEARCH' | translate">
      </div>
      <button class="btn-secondary" type="button" (click)="openCreateModule()" [disabled]="hasPendingOrder()">
        <i class="fa-solid fa-layer-group"></i>
        {{ 'ASSISTANCE.NEW_MODULE' | translate }}
      </button>
    </div>
  </ng-container>

  <!-- ── Loading skeleton ─────────────────────────────────────── -->
  <div *ngIf="loading()" class="assist__skeleton">
    <div class="assist__skel-row" *ngFor="let _ of [1,2,3]"></div>
  </div>

  <!-- ── Error ────────────────────────────────────────────────── -->
  <app-error-state *ngIf="!loading() && errored()"
                   titleKey="ASSISTANCE.ERROR_TITLE"
                   messageKey="ASSISTANCE.LOAD_ERROR"
                   (retry)="reload()"></app-error-state>

  <!-- ── Modules ──────────────────────────────────────────────── -->
  <ng-container *ngIf="!loading() && !errored()">
    <div class="mods" *ngIf="visibleModules().length > 0; else emptyTpl">
      <article class="mod" *ngFor="let v of visibleModules(); trackBy: trackModule"
               [class.mod--open]="isOpen(v.module)"
               [class.mod--admin]="v.module.audience === 'Admin'">

        <header class="mod__head" (click)="toggle(v.module)">
          <span class="mod__icon" [ngClass]="'aud--' + audienceClass(v.module.audience)">
            <i class="fa-solid fa-layer-group"></i>
          </span>
          <div class="mod__info">
            <div class="mod__kicker">{{ 'ASSISTANCE.MODULE_N' | translate: { n: v.module.displayOrder } }}</div>
            <h3 class="mod__title">{{ v.module.title }}</h3>
            <p class="mod__desc" *ngIf="v.module.description">{{ v.module.description }}</p>
            <div class="mod__meta" *ngIf="summarize(v.module.resources) as s">
              <span class="meta-item" *ngIf="s.videos">
                <i class="fa-solid fa-circle-play"></i>{{ 'ASSISTANCE.COUNT_VIDEOS' | translate: { count: s.videos } }}
              </span>
              <span class="meta-item" *ngIf="s.documents">
                <i class="fa-solid fa-file-lines"></i>{{ 'ASSISTANCE.COUNT_DOCUMENTS' | translate: { count: s.documents } }}
              </span>
              <span class="meta-item" *ngIf="s.links">
                <i class="fa-solid fa-link"></i>{{ 'ASSISTANCE.COUNT_LINKS' | translate: { count: s.links } }}
              </span>
              <span class="meta-item" *ngIf="v.module.resources.length === 0">
                {{ 'ASSISTANCE.COUNT_RESOURCES' | translate: { count: 0 } }}
              </span>
            </div>
          </div>
          <div class="mod__actions" (click)="$event.stopPropagation()">
            <button class="act-btn act-btn--add" type="button" (click)="openCreateResource(v.module)"
                    [disabled]="hasPendingOrder()" [title]="'ASSISTANCE.ADD_TO_MODULE' | translate">
              <i class="fa-solid fa-plus"></i>
            </button>
            <button class="act-btn act-btn--edit" type="button" (click)="openEditModule(v.module)"
                    [disabled]="hasPendingOrder()" [title]="'ASSISTANCE.EDIT_MODULE' | translate">
              <i class="fa-solid fa-pen"></i>
            </button>
            <button class="act-btn act-btn--del" type="button" (click)="askArchiveModule(v.module)"
                    [disabled]="hasPendingOrder()" [title]="'ASSISTANCE.ARCHIVE' | translate">
              <i class="fa-solid fa-trash"></i>
            </button>
            <button class="mod__chev" type="button" (click)="toggle(v.module)" [attr.aria-expanded]="isOpen(v.module)">
              <i class="fa-solid fa-chevron-down"></i>
            </button>
          </div>
        </header>

        <div class="mod__body" *ngIf="isOpen(v.module)">
          <div class="res" *ngFor="let r of v.shown; let first = first; let last = last; trackBy: trackResource">
            <span class="res__num">{{ r.displayOrder }}</span>
            <span class="res__type" [ngClass]="'aud--' + audienceClass(v.module.audience)">
              <i [class]="kindIcon(r)"></i>
            </span>
            <div class="res__info">
              <div class="res__title">{{ r.title }}</div>
              <div class="res__sub">
                <span class="mono" *ngIf="r.fileSizeBytes">{{ formatSize(r.fileSizeBytes) }}</span>
                <span *ngIf="r.fileSizeBytes && r.description"> · </span>
                <span *ngIf="r.description">{{ r.description }}</span>
              </div>
            </div>
            <div class="res__actions">
              <button class="act-btn act-btn--move" type="button" (click)="move(v.module, r, -1)"
                      [disabled]="first || !!query().trim() || savingOrder()" [title]="'ASSISTANCE.MOVE_UP' | translate">
                <i class="fa-solid fa-arrow-up"></i>
              </button>
              <button class="act-btn act-btn--move" type="button" (click)="move(v.module, r, 1)"
                      [disabled]="last || !!query().trim() || savingOrder()" [title]="'ASSISTANCE.MOVE_DOWN' | translate">
                <i class="fa-solid fa-arrow-down"></i>
              </button>
              <button *ngIf="r.hasFile || r.previewUrl || r.externalUrl" class="act-btn act-btn--view" type="button"
                      (click)="openPlayer(r, v.module)" [title]="'ASSISTANCE.PREVIEW' | translate">
                <i class="fa-solid fa-eye"></i>
              </button>
              <button *ngIf="r.hasFile" class="act-btn act-btn--dl" type="button"
                      (click)="onDownload(r)" [title]="'ASSISTANCE.DOWNLOAD' | translate">
                <i class="fa-solid fa-download"></i>
              </button>
              <button class="act-btn act-btn--edit" type="button" [disabled]="hasPendingOrder()"
                      (click)="openEditResource(r)" [title]="'COMMON.EDIT' | translate">
                <i class="fa-solid fa-pen"></i>
              </button>
              <button class="act-btn act-btn--del" type="button" [disabled]="hasPendingOrder()"
                      (click)="askArchiveResource(r)" [title]="'ASSISTANCE.ARCHIVE' | translate">
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          </div>

          <div class="mod__empty" *ngIf="v.module.resources.length === 0">{{ 'ASSISTANCE.MODULE_EMPTY' | translate }}</div>

          <div class="order-bar" *ngIf="isDirty(v.module)">
            <span class="order-bar__text">
              <i class="fa-solid fa-arrows-up-down"></i> {{ 'ASSISTANCE.ORDER_CHANGED' | translate }}
            </span>
            <button class="btn-secondary btn-sm" type="button" (click)="cancelOrder(v.module)" [disabled]="savingOrder()">
              {{ 'COMMON.CANCEL' | translate }}
            </button>
            <button class="btn-primary btn-sm" type="button" (click)="saveOrder(v.module)" [disabled]="savingOrder()">
              <i class="fa-solid fa-spinner fa-spin" *ngIf="savingOrder()"></i>
              {{ 'ASSISTANCE.SAVE_ORDER' | translate }}
            </button>
          </div>

          <button class="mod__add" type="button" (click)="openCreateResource(v.module)" [disabled]="hasPendingOrder()">
            <i class="fa-solid fa-plus"></i> {{ 'ASSISTANCE.ADD_TO_MODULE' | translate }}
          </button>
        </div>
      </article>
    </div>

    <ng-template #emptyTpl>
      <div class="assist__empty">
        <ng-container *ngIf="query().trim(); else noModules">
          <div class="assist__empty-icon"><i class="fa-solid fa-magnifying-glass"></i></div>
          <p>{{ 'ASSISTANCE.NO_MATCH' | translate }}</p>
        </ng-container>
        <ng-template #noModules>
          <div class="assist__empty-icon"><i class="fa-solid fa-layer-group"></i></div>
          <h3>{{ 'ASSISTANCE.NO_MODULES_TITLE' | translate }}</h3>
          <p>{{ 'ASSISTANCE.NO_MODULES_DESC' | translate }}</p>
          <button class="btn-primary assist__empty-cta" type="button" (click)="openCreateModule()">
            <i class="fa-solid fa-layer-group"></i> {{ 'ASSISTANCE.NEW_MODULE' | translate }}
          </button>
        </ng-template>
      </div>
    </ng-template>
  </ng-container>

</div>

<!-- ════════════════════════════════════════════════════════════
     LECTEUR
════════════════════════════════════════════════════════════ -->
<app-assistance-player *ngIf="playerResource() as r"
                       [resource]="r"
                       [series]="playerModule()?.resources ?? []"
                       [moduleTitle]="playerModule()?.title ?? ''"
                       (closed)="closePlayer()"
                       (navigate)="playerResource.set($event)"
                       (download)="onDownload($event)"></app-assistance-player>

<!-- ════════════════════════════════════════════════════════════
     RESSOURCE : CRÉATION / MODIFICATION
════════════════════════════════════════════════════════════ -->
<div class="assist-modal-backdrop" *ngIf="resourceFormOpen()" (click)="closeResourceForm()"></div>
<aside class="assist-modal" *ngIf="resourceFormOpen()" role="dialog" aria-modal="true">
  <header class="modal__head">
    <h2 class="modal__title">
      {{ (editResource ? 'ASSISTANCE.EDIT_TITLE' : 'ASSISTANCE.CREATE_TITLE') | translate }}
    </h2>
    <button class="modal__close" type="button" (click)="closeResourceForm()" [attr.aria-label]="'ACTIONS.CLOSE' | translate">
      <i class="fa-solid fa-xmark"></i>
    </button>
  </header>
  <form class="modal__body" (ngSubmit)="submitResource()">
    <div class="form-field">
      <label for="res-title">{{ 'ASSISTANCE.FORM.TITLE' | translate }} *</label>
      <input id="res-title" type="text" [(ngModel)]="resourceForm.title" name="title" required maxlength="255">
    </div>
    <div class="form-field">
      <label for="res-desc">{{ 'ASSISTANCE.FORM.DESCRIPTION' | translate }}</label>
      <textarea id="res-desc" [(ngModel)]="resourceForm.description" name="description" rows="3"></textarea>
    </div>
    <div class="form-row">
      <div class="form-field">
        <label for="res-module">{{ 'ASSISTANCE.FORM.MODULE' | translate }} *</label>
        <select id="res-module" [(ngModel)]="resourceForm.moduleId" name="moduleId"
                (ngModelChange)="onResourceModuleChange($event)" required>
          <optgroup *ngFor="let a of audiences" [label]="audienceLabel(a) | translate">
            <option *ngFor="let m of modulesOf(a)" [ngValue]="m.id">{{ m.title }}</option>
          </optgroup>
        </select>
      </div>
      <div class="form-field">
        <label for="res-position">{{ 'ASSISTANCE.FORM.POSITION' | translate }}</label>
        <input id="res-position" type="number" [(ngModel)]="resourceForm.displayOrder" name="displayOrder" min="1">
      </div>
    </div>
    <small class="form-hint">{{ 'ASSISTANCE.FORM.MODULE_HINT' | translate }}</small>

    <div class="form-callout" *ngIf="resourceModule()?.audience === 'Admin'">
      <i class="fa-solid fa-user-shield"></i>
      <span>{{ 'ASSISTANCE.ADMIN_CALLOUT' | translate }}</span>
    </div>

    <!-- Fichier ou lien — seulement à la création -->
    <ng-container *ngIf="!editResource">
      <div class="form-field">
        <label for="res-file">{{ 'ASSISTANCE.FORM.FILE' | translate }}</label>
        <input id="res-file" type="file" (change)="onFileSelected($event)" class="file-input"
               [disabled]="!!resourceForm.externalUrl.trim()">
        <small class="form-hint" *ngIf="selectedFile">{{ selectedFile.name }} ({{ formatSize(selectedFile.size) }})</small>
      </div>
      <div class="form-separator">{{ 'ASSISTANCE.FORM.OR' | translate }}</div>
      <div class="form-field">
        <label for="res-url">{{ 'ASSISTANCE.FORM.EXTERNAL_URL' | translate }}</label>
        <input id="res-url" type="url" [(ngModel)]="resourceForm.externalUrl" name="externalUrl"
               placeholder="https://youtube.com/..." [disabled]="!!selectedFile">
      </div>
    </ng-container>

    <div *ngIf="uploading()" class="upload-progress">
      <div class="upload-progress__bar">
        <div class="upload-progress__fill" [style.width.%]="uploadPercent() ?? 0"></div>
      </div>
      <span class="upload-progress__label">{{ uploadPercent() ?? 0 }}%</span>
    </div>

    <footer class="modal__foot">
      <button type="button" class="btn-secondary" (click)="closeResourceForm()" [disabled]="saving() || uploading()">
        {{ 'COMMON.CANCEL' | translate }}
      </button>
      <button type="submit" class="btn-primary" [disabled]="saving() || uploading() || !canSubmitResource()">
        <i class="fa-solid fa-spinner fa-spin" *ngIf="saving() || uploading()"></i>
        {{ 'COMMON.SAVE' | translate }}
      </button>
    </footer>
  </form>
</aside>

<!-- ════════════════════════════════════════════════════════════
     MODULE : CRÉATION / MODIFICATION
════════════════════════════════════════════════════════════ -->
<div class="assist-modal-backdrop" *ngIf="moduleFormOpen()" (click)="closeModuleForm()"></div>
<aside class="assist-modal" *ngIf="moduleFormOpen()" role="dialog" aria-modal="true">
  <header class="modal__head">
    <h2 class="modal__title">
      {{ (editModule ? 'ASSISTANCE.EDIT_MODULE' : 'ASSISTANCE.CREATE_MODULE') | translate }}
    </h2>
    <button class="modal__close" type="button" (click)="closeModuleForm()" [attr.aria-label]="'ACTIONS.CLOSE' | translate">
      <i class="fa-solid fa-xmark"></i>
    </button>
  </header>
  <form class="modal__body" (ngSubmit)="submitModule()">
    <div class="form-field">
      <label for="mod-title">{{ 'ASSISTANCE.FORM.MODULE_TITLE' | translate }} *</label>
      <input id="mod-title" type="text" [(ngModel)]="moduleForm.title" name="title" required maxlength="255">
    </div>
    <div class="form-field">
      <label for="mod-desc">{{ 'ASSISTANCE.FORM.DESCRIPTION' | translate }}</label>
      <textarea id="mod-desc" [(ngModel)]="moduleForm.description" name="description" rows="2" maxlength="2000"></textarea>
    </div>
    <div class="form-row">
      <div class="form-field">
        <label for="mod-audience">{{ 'ASSISTANCE.FORM.AUDIENCE' | translate }} *</label>
        <select id="mod-audience" [(ngModel)]="moduleForm.audience" name="audience" required
                (ngModelChange)="onModuleAudienceChange($event)">
          <option *ngFor="let a of audiences" [ngValue]="a">{{ audienceLabel(a) | translate }}</option>
        </select>
      </div>
      <div class="form-field">
        <label for="mod-position">{{ 'ASSISTANCE.FORM.POSITION' | translate }}</label>
        <input id="mod-position" type="number" [(ngModel)]="moduleForm.displayOrder" name="displayOrder" min="1">
      </div>
    </div>
    <small class="form-hint">{{ 'ASSISTANCE.FORM.AUDIENCE_HINT' | translate }}</small>

    <div class="form-callout" *ngIf="moduleForm.audience === 'Admin'">
      <i class="fa-solid fa-user-shield"></i>
      <span>{{ 'ASSISTANCE.TAB_NOTE_ADMIN' | translate }}</span>
    </div>

    <footer class="modal__foot">
      <button type="button" class="btn-secondary" (click)="closeModuleForm()" [disabled]="saving()">
        {{ 'COMMON.CANCEL' | translate }}
      </button>
      <button type="submit" class="btn-primary" [disabled]="saving() || !moduleForm.title.trim()">
        <i class="fa-solid fa-spinner fa-spin" *ngIf="saving()"></i>
        {{ 'COMMON.SAVE' | translate }}
      </button>
    </footer>
  </form>
</aside>

<!-- ════════════════════════════════════════════════════════════
     ARCHIVAGE
════════════════════════════════════════════════════════════ -->
<div class="assist-modal-backdrop" *ngIf="deleteTarget()" (click)="deleteTarget.set(null)"></div>
<aside class="assist-modal assist-modal--sm" *ngIf="deleteTarget()" role="alertdialog" aria-modal="true">
  <header class="modal__head">
    <h2 class="modal__title">{{ 'ASSISTANCE.ARCHIVE' | translate }}</h2>
  </header>
  <div class="modal__body">
    <p class="modal__text" *ngIf="deleteMessage() as msg">{{ msg.key | translate: msg.params }}</p>
    <footer class="modal__foot">
      <button type="button" class="btn-secondary" (click)="deleteTarget.set(null)" [disabled]="deleting()">
        {{ 'COMMON.CANCEL' | translate }}
      </button>
      <button type="button" class="btn-danger" (click)="confirmDelete()" [disabled]="deleting()">
        <i class="fa-solid fa-spinner fa-spin" *ngIf="deleting()"></i>
        {{ 'ASSISTANCE.ARCHIVE' | translate }}
      </button>
    </footer>
  </div>
</aside>
